import { useEffect } from 'react';
import { SITE } from '@/lib/site';

const BASE = SITE.domain;
const PROD_HOSTS = ['hyenamind.com', 'www.hyenamind.com'];
const DEFAULT_OG_IMAGE = `${BASE}/assets/brand/og-default.jpg`;

function upsertMeta(kind, key, content) {
  let el = document.head.querySelector(`meta[${kind}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(kind, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export default function SEO({ title, description, path = '/', jsonLd, image, noindex }) {
  useEffect(() => {
    // Canonical convention: non-trailing-slash is the served URL (trailing-slash requests redirect to it).
    const url = path === '/' ? `${BASE}/` : `${BASE}${path.replace(/\/+$/, '')}`;
    document.title = title;

    if (process.env.NODE_ENV !== 'production') {
      if (!title) console.warn('[SEO] missing title:', path);
      if (!description) console.warn('[SEO] missing meta description:', path);
      if (title && title.length > 60) console.warn('[SEO] title over 60 chars:', path);
      if (description && description.length > 165) console.warn('[SEO] description over 165 chars:', path);
    }

    upsertMeta('name', 'description', description);

    // A page that opts out of indexing (404, thank-you) has no canonical URL to declare.
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (noindex) {
      if (canonical) canonical.remove();
    } else {
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', url);
    }

    // Staging/preview protection: only the production host may be indexed.
    const isProdHost = PROD_HOSTS.includes(window.location.hostname);
    const shouldNoindex = noindex || !isProdHost;
    let robots = document.head.querySelector('meta[name="robots"]');
    if (shouldNoindex) {
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      robots.setAttribute('content', 'noindex, nofollow');
    } else if (robots) {
      robots.remove();
    }

    const ogImage = image || DEFAULT_OG_IMAGE;
    const og = {
      'og:title': title,
      'og:description': description,
      'og:url': url,
      'og:image': ogImage,
      'og:type': 'website',
      'og:site_name': 'HyenaMind',
    };
    Object.entries(og).forEach(([key, content]) => upsertMeta('property', key, content));

    const twitter = {
      'twitter:card': 'summary_large_image',
      'twitter:title': title,
      'twitter:description': description,
      'twitter:image': ogImage,
    };
    Object.entries(twitter).forEach(([key, content]) => upsertMeta('name', key, content));

    document.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());
    const blocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    blocks.forEach((obj) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-seo-jsonld', 'true');
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
    });
  }, [title, description, path, jsonLd, image, noindex]);

  return null;
}
